import { useEffect } from 'react';

interface PerformanceNavigationEntry extends PerformanceEntry {
  responseStart?: number;
}

interface PerformanceFirstInputEntry extends PerformanceEntry {
  processingStart: number;
}

interface PerformanceLayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface WebVitalsEntry extends PerformanceEntry {
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
}

export const usePerformanceMonitoring = () => {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    const metrics: PerformanceMetrics = {};

    // Track Web Vitals
    const trackWebVital = (entry: WebVitalsEntry) => {
      switch (entry.name) {
        case 'LCP':
          metrics.lcp = entry.value;
          break;
        case 'FID':
          metrics.fid = entry.value;
          break;
        case 'CLS':
          metrics.cls = entry.value;
          break;
        case 'FCP':
          metrics.fcp = entry.value;
          break;
        case 'TTFB':
          metrics.ttfb = entry.value;
          break;
      }

      // Log metrics in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`${entry.name}: ${entry.value} (${entry.rating})`);
      }
    };

    // Observe performance entries
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'largest-contentful-paint') {
              metrics.lcp = entry.startTime;
            }
            if (entry.entryType === 'first-input') {
              const firstInputEntry = entry as PerformanceFirstInputEntry;
              metrics.fid = firstInputEntry.processingStart - firstInputEntry.startTime;
            }
            if (entry.entryType === 'layout-shift') {
              const layoutShiftEntry = entry as PerformanceLayoutShiftEntry;
              if (!layoutShiftEntry.hadRecentInput) {
                metrics.cls = (metrics.cls || 0) + layoutShiftEntry.value;
              }
            }
            if (entry.entryType === 'paint' && entry.name === 'first-contentful-paint') {
              metrics.fcp = entry.startTime;
            }
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationEntry;
              metrics.ttfb = navEntry.responseStart || 0;
            }
          });
        });

        observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift', 'paint', 'navigation'] });

        return () => observer.disconnect();
      } catch (error) {
        console.warn('Performance monitoring not supported:', error);
      }
    }

    // Fallback: Use basic performance API
    const logNavigationTiming = () => {
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        const navigationStart = timing.navigationStart;
        
        setTimeout(() => {
          const loadComplete = timing.loadEventEnd - navigationStart;
          const domReady = timing.domContentLoadedEventEnd - navigationStart;
          const firstPaint = performance.getEntriesByType('paint')[0]?.startTime || 0;
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Performance Metrics:', {
              loadComplete: `${loadComplete}ms`,
              domReady: `${domReady}ms`,
              firstPaint: `${firstPaint}ms`,
            });
          }
        }, 0);
      }
    };

    if (document.readyState === 'complete') {
      logNavigationTiming();
    } else {
      window.addEventListener('load', logNavigationTiming);
    }

    // Error boundary for performance monitoring
    window.addEventListener('error', (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Performance monitoring error:', event.error);
      }
    });

  }, []);
};